import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import type { StringSchema } from "yup";
import type {
  SolicitudCreditoFieldName,
  SolicitudCreditoStepsNames,
  TabsConfigResponseFormConfigField,
  TabsConfigResponseFormConfigSolicitudCredito,
  TabsConfigResponseFormConfigStep,
} from "@interfaces/response";

type TaxSystemTypeCode = "PM" | "PFAE" | "PF";

const enabledField = (required: boolean): TabsConfigResponseFormConfigField => ({
  enabled: true,
  required,
});

const domicilioField: TabsConfigResponseFormConfigField = {
  enabled: true,
};

const DEFAULT_STEP_1_PM: TabsConfigResponseFormConfigStep = {
  code: "EMPRESA_Y_DOMICILIO",
  enabled: true,
  fields: {
    country_code: enabledField(true),
    cell_phone_number: enabledField(true),
    giro: enabledField(true),
    descripcion: enabledField(true),
    domicilio_operativo: domicilioField,
  },
};

const DEFAULT_STEP_1_PF: TabsConfigResponseFormConfigStep = {
  code: "EMPRESA_Y_DOMICILIO",
  enabled: true,
  fields: {
    country_code: enabledField(true),
    phone: enabledField(true),
    email: enabledField(true),
    ocupacion: enabledField(true),
    domicilio_particular: domicilioField,
  },
};

const DEFAULT_STEP_1_PFAE: TabsConfigResponseFormConfigStep = {
  code: "EMPRESA_Y_DOMICILIO",
  enabled: true,
  fields: {
    curp: enabledField(true),
    ocupacion: enabledField(true),
    estado_civil_id: enabledField(true),
    matrimonial_regimes_id: enabledField(true),
    conyuge_id: enabledField(true),
    conyuge_name: enabledField(true),
    conyuge_last_name: enabledField(true),
    conyuge_last_name_2: enabledField(false),
    domicilio_operativo: domicilioField,
    domicilio_particular: domicilioField,
  },
};

const DEFAULT_STEPS_BY_TAX_SYSTEM: Record<
  TaxSystemTypeCode,
  Partial<Record<SolicitudCreditoStepsNames, TabsConfigResponseFormConfigStep>>
> = {
  PM: {
    step_1: DEFAULT_STEP_1_PM,
  },
  PF: {
    step_1: DEFAULT_STEP_1_PF,
  },
  PFAE: {
    step_1: DEFAULT_STEP_1_PFAE,
  },
};

const getDefaultStepConfig = (
  taxSystemType: TaxSystemTypeCode,
  step: SolicitudCreditoStepsNames,
): TabsConfigResponseFormConfigStep | undefined => DEFAULT_STEPS_BY_TAX_SYSTEM[taxSystemType][step];

const getSolicitudCreditoStepConfig = (
  config: TabsConfigResponseFormConfigSolicitudCredito | undefined,
  step: SolicitudCreditoStepsNames,
): TabsConfigResponseFormConfigStep | undefined => config?.[step];

const hasStepFields = (stepConfig: TabsConfigResponseFormConfigStep | undefined): boolean =>
  Boolean(stepConfig?.fields && Object.keys(stepConfig.fields).length > 0);

const isStepFieldVisible = (
  stepConfig: TabsConfigResponseFormConfigStep | undefined,
  field: SolicitudCreditoFieldName,
): boolean => {
  if (!hasStepFields(stepConfig)) return true;
  if (stepConfig?.enabled === false) return false;
  return stepConfig?.fields?.[field]?.enabled ?? false;
};

const isStepFieldRequired = (
  stepConfig: TabsConfigResponseFormConfigStep | undefined,
  field: SolicitudCreditoFieldName,
  fallback: boolean,
): boolean => {
  if (!isStepFieldVisible(stepConfig, field)) return false;
  const fieldConfig = stepConfig?.fields?.[field];
  if (fieldConfig?.required === undefined) return fallback;
  return fieldConfig.required;
};

export const useSolicitudCreditoForm = (step: SolicitudCreditoStepsNames) => {
  const solicitudCredito = useSelector((state: RootState) => state.bofu.bofu_tabs_obj.empresa.forms.solicitud_credito);
  const taxSystemType = useSelector((state: RootState) => state.register.application.tax_system_type);

  const apiStepConfig = getSolicitudCreditoStepConfig(solicitudCredito?.config, step);
  const stepConfig = hasStepFields(apiStepConfig) ? apiStepConfig : getDefaultStepConfig(taxSystemType, step);

  const isVisible = (field: SolicitudCreditoFieldName) => isStepFieldVisible(stepConfig, field);

  const isRequired = (field: SolicitudCreditoFieldName, fallback: boolean) =>
    isStepFieldRequired(stepConfig, field, fallback);

  const requiredField = <T extends StringSchema>(
    schema: T,
    field: SolicitudCreditoFieldName,
    fallback: boolean,
    message = "Requerido",
  ) => (isRequired(field, fallback) ? schema.required(message) : schema.notRequired());

  return {
    isVisible,
    isRequired,
    requiredField,
    isStepEnabled: stepConfig?.enabled ?? true,
    isFormEnabled: solicitudCredito?.enabled ?? true,
    stepConfig,
  };
};

export default useSolicitudCreditoForm;
